import React, { useState, useEffect } from "react";
import {
    ChevronDown,
    Mail,
    Phone,
    MapPin,
    Clock,
    Send,
    CheckCircle,
    Star,
} from "lucide-react";
import Footer from "../layout/Footer";
import Navbar from "../layout/Navbar";

const Contact = () => {
    const [scrollY, setScrollY] = useState(0);
    const [isVisible, setIsVisible] = useState({});
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        company: "",
        phone: "",
        subject: "",
        message: "",
        service: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState("");
    const [isMapOpen, setIsMapOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        const observers = new Map();
        const elements = document.querySelectorAll("[data-animate]");

        elements.forEach((el, index) => {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setIsVisible((prev) => ({ ...prev, [index]: true }));
                    }
                },
                { threshold: 0.1 }
            );
            observer.observe(el);
            observers.set(index, observer);
        });

        return () => observers.forEach((observer) => observer.disconnect());
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        setTimeout(() => {
            setIsSubmitting(false);
            setSubmitStatus("success");
            setFormData({
                name: "",
                email: "",
                company: "",
                phone: "",
                subject: "",
                message: "",
                service: "",
            });

            setTimeout(() => setSubmitStatus(""), 3000);
        }, 2000);
    };

    const contactInfo = [
        {
            icon: <Mail className="w-6 h-6" />,
            title: "Email Address",
            details: ["onehexatech@gmail.com"],
        },
        {
            icon: <Phone className="w-6 h-6" />,
            title: "Phone Numbers",
            details: ["+91 94273 88748", "+91 89808 29331"],
        },
        {
            icon: <MapPin className="w-6 h-6" />,
            title: "Office Address",
            details: [
                "Office No. 9, Noor Plaza, NH-27, Wankaner",
                "Morbi, Gujarat 363621",
            ],
        },
        {
            icon: <Clock className="w-6 h-6" />,
            title: "Business Hours",
            details: ["Mon - Fri: 9:00 AM - 6:00 PM", "Sat: 10:00 AM - 4:00 PM"],
        },
    ];

    const services = [
        "Custom Software Development",
        "Mobile App Development",
        "AI & Automation Solutions",
        "Cloud Migration",
        "Product Maintenance & Support",
        "Enterprise Solutions",
        "Other",
    ];

    const quickStats = [
        { number: "24/7", label: "Support Available" },
        { number: "<2hr", label: "Response Time" },
        { number: "10+", label: "Happy Clients" },
        { number: "99%", label: "Satisfaction Rate" },
    ];

    return (
        <div className="min-h-screen bg-white text-gray-800">
            <Navbar />

            {/* Hero Section */}
            <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-b from-gray-50 to-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center">
                        <div
                            className="animate-fade-in-up"
                            style={{ transform: `translateY(${scrollY * 0.1}px)` }}
                        >
                            <h1 className="text-5xl md:text-7xl font-bold mt-16 text-gray-800 leading-tight">
                                Get In
                                <br />
                                <span className="text-green-500 inline-block animate-pulse">Touch</span>
                            </h1>
                            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed capitalize">
                                Ready to build powerful products with <span className="text-green-500 font-semibold">OneHexa Tech</span> designed to transform your business.
                            </p>
                        </div>

                        {/* Floating Elements */}
                        {/* <div className="relative">
                            <div className="absolute -top-20 -left-20 w-40 h-40 bg-green-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-bounce" style={{ animationDelay: '0s', animationDuration: '3s' }}></div>
                            <div className="absolute -top-20 -right-20 w-40 h-40 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-bounce" style={{ animationDelay: '1s', animationDuration: '3s' }}></div>
                            <div className="absolute -bottom-20 left-1/2 w-40 h-40 bg-purple-50 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-bounce" style={{ animationDelay: '2s', animationDuration: '3s' }}></div>
                        </div> */}

                        {/* Scroll Indicator */}
                        <div className="animate-bounce mt-16">
                            <ChevronDown className="w-8 h-8 text-green-500 mx-auto" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Quick Stats */}
            <section className="py-16 bg-gray-900" data-animate>
                <div
                    className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${isVisible[0]
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 translate-y-10"
                        }`}
                >
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                        {quickStats.map((stat, index) => (
                            <div key={index} className="text-white">
                                <div className="text-4xl md:text-5xl font-bold mb-2 text-green-400 animate-pulse">
                                    {stat.number}
                                </div>
                                <div className="text-gray-300 text-lg">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact Form & Info */}
            <section
                className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50"
                data-animate
            >
                <div
                    className={`max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 transition-all duration-1000 delay-200 ${isVisible[1]
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 translate-y-10"
                        }`}
                >
                    {/* Contact Form */}
                    <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                        <h3 className="text-2xl font-bold text-gray-800 mb-6">
                            Send us a Message
                        </h3>

                        {submitStatus === "success" && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center space-x-3">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                <span className="text-green-800">
                                    Message sent successfully! We'll get back to you soon.
                                </span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Full Name *
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                        placeholder="Enter your name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Email Address *
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                        placeholder="Enter your email"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Company
                                    </label>
                                    <input
                                        type="text"
                                        name="company"
                                        value={formData.company}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                        placeholder="Company name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Phone
                                    </label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                        placeholder="Phone number"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Service
                                </label>
                                <select
                                    name="service"
                                    value={formData.service}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                >
                                    <option value="">Select a Service</option>
                                    {services.map((s, idx) => (
                                        <option key={idx} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Subject
                                </label>
                                <input
                                    type="text"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                    placeholder="Subject"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Message *
                                </label>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    required
                                    rows={5}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                    placeholder="Tell us more..."
                                ></textarea>
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white py-4 px-6 rounded-lg font-semibold transition flex items-center justify-center"
                            >
                                {isSubmitting ? (
                                    "Sending..."
                                ) : (
                                    <>
                                        <Send className="w-5 h-5 mr-2" /> Send Message
                                    </>
                                )}
                            </button>
                        </form>
                    </div>

                    {/* Contact Information */}
                    <div className="space-y-8">
                        {contactInfo.map((info, index) => (
                            <div
                                key={index}
                                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-gray-100 hover:border-green-200"
                            >
                                <div className="flex items-start space-x-4">
                                    <div className="w-12 h-12 flex items-center justify-center bg-green-50 rounded-lg">
                                        {info.icon}
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-800 mb-2">
                                            {info.title}
                                        </h3>
                                        {info.details.map((detail, idx) => (
                                            <p key={idx} className="text-gray-600">
                                                {detail}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section
                className="py-20 bg-white px-4 sm:px-6 lg:px-8"
                data-animate
            >
                <div
                    className={`max-w-4xl mx-auto transition-all duration-1000 delay-300 ${isVisible[2]
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 translate-y-10"
                        }`}
                >
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                            Common <span className="text-green-500">Questions</span>
                        </h2>
                        <p className="text-xl text-gray-600">
                            Find quick answers about our product-based software solutions.
                        </p>
                    </div>

                    <div className="space-y-6">
                        {[
                            {
                                question: "Do you build custom software products?",
                                answer:
                                    "Yes! We specialize in developing tailor-made software solutions that align with your business goals.",
                            },
                            {
                                question: "Can you scale products as our company grows?",
                                answer:
                                    "Absolutely. We design scalable solutions so your product grows with your business.",
                            },
                            {
                                question: "What industries do you serve?",
                                answer:
                                    "We work across multiple industries including e-commerce, healthcare, manufacturing, and fintech.",
                            },
                            {
                                question: "Do you provide long-term maintenance?",
                                answer:
                                    "Yes, our team offers ongoing support, product updates, and bug fixes to keep your software reliable.",
                            },
                        ].map((faq, index) => (
                            <div
                                key={index}
                                className="bg-gray-50 rounded-xl p-6 hover:bg-white hover:shadow-lg transition border border-transparent hover:border-green-200"
                            >
                                <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
                                    <Star className="w-5 h-5 text-green-500 mr-2" />
                                    {faq.question}
                                </h3>
                                <p className="text-gray-600 pl-7">{faq.answer}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Google Map Section */}
            <section
                className="py-20 bg-gray-900 px-4 sm:px-6 lg:px-8 text-center"
                data-animate
            >
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                    Visit Our <span className="text-green-400">Office</span>
                </h2>
                <p className="text-xl text-gray-300 mb-8">
                    Find us easily on the map below.
                </p>

                <div className="max-w-full mx-auto rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                    <iframe
                        title="OneHexa Office Location"
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3683.5872239331834!2d70.96679687385056!3d22.594536632168793!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3959bd00680cae19%3A0x1334b6bad477c2ab!2sOneHexa%20Tech!5e0!3m2!1sen!2sin!4v1755841487101!5m2!1sen!2sin"
                        width="100%"
                        height="600"   // 👈 yaha badha diya
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        className="rounded-2xl"
                    ></iframe>
                </div>

            </section>

            <Footer />
            <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out;
        }
      `}</style>
        </div >
    );
};

export default Contact;
